import { useMemo } from 'react';

import { useGetBusinessRulesQuery } from 'features/notification-config/services/businessRules.api';
import type { BusinessRuleRow } from 'features/notification-config/types/businessRule';

import {
  EMPTY_MORTGAGE_RULES_STATE,
  MORTGAGE_BOOLEAN_RULE_NAMES,
  MORTGAGE_PRE_EVALUATION_CONFIG,
  MORTGAGE_RULE_NAMES,
  type MortgagePreEvaluationConfig,
  type MortgageRulesState,
  parsePreEvaluationConfig,
} from '../model/mortgageRules';

export interface ProjectMortgageRules {
  rules: MortgageRulesState;
  /** The stored row per rule: a toggle writes with PATCH when there is one, POST when there is not. */
  rows: Partial<Record<string, BusinessRuleRow>>;
  config: MortgagePreEvaluationConfig;
  configRow: BusinessRuleRow | null;
}

export interface UseMortgageRulesResult {
  byProject: Map<number, ProjectMortgageRules>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

const EMPTY_PROJECT_RULES: ProjectMortgageRules = {
  rules: EMPTY_MORTGAGE_RULES_STATE,
  rows: {},
  config: parsePreEvaluationConfig(null),
  configRow: null,
};

/* Disabled rows are dropped on the way in, so by here a rule is on when its value is. */
const isRuleOn = (row: BusinessRuleRow | undefined): boolean => row?.value === true;

/**
 * Reads the financing rules for a set of projects in one request. Every project is asked for at its
 * own id and nothing is inherited from a master, the same way the agent resolves them.
 */
const useMortgageRules = (entityIds: number[]): UseMortgageRulesResult => {
  const query = useGetBusinessRulesQuery(
    {
      names: [...MORTGAGE_RULE_NAMES],
      entityIds,
      entityType: 'Project',
      limit: MORTGAGE_RULE_NAMES.length * Math.max(entityIds.length, 1),
    },
    { skip: entityIds.length === 0 }
  );

  /* Callers build the id list inline, so it carries a new identity on every render. Keying the memo
     on its contents is what keeps the parsed config stable — consumers resync their draft off it. */
  const idsKey = entityIds.join(',');

  const byProject = useMemo(() => {
    const result = new Map<number, ProjectMortgageRules>();
    const ids = idsKey ? idsKey.split(',').map(Number) : [];
    ids.forEach((entityId) => result.set(entityId, EMPTY_PROJECT_RULES));

    /*
     * Two filters, both mirroring how the agent resolves a rule. It reads only enabled rows, and the
     * unique indexes are partial on `enabled`, so duplicates are possible; when there are, it keeps the
     * highest id. Taking the last row of this list instead would keep the oldest, since the endpoint
     * orders createdAt DESC, id DESC — and the screen would report a different rule than the one running.
     */
    (query.data?.data ?? []).forEach((row) => {
      if (typeof row.entityId !== 'number' || !row.name || row.enabled === false) return;
      const current = result.get(row.entityId);
      if (!current) return;
      const kept = current.rows[row.name];
      if (kept && kept.id > row.id) return;
      result.set(row.entityId, { ...current, rows: { ...current.rows, [row.name]: row } });
    });

    result.forEach((entry, entityId) => {
      const configRow = entry.rows[MORTGAGE_PRE_EVALUATION_CONFIG] ?? null;
      result.set(entityId, {
        ...entry,
        rules: MORTGAGE_BOOLEAN_RULE_NAMES.reduce(
          (acc, name) => ({ ...acc, [name]: isRuleOn(entry.rows[name]) }),
          {} as MortgageRulesState
        ),
        config: parsePreEvaluationConfig(configRow?.value ?? null),
        configRow,
      });
    });

    return result;
  }, [idsKey, query.data]);

  return { byProject, isLoading: query.isLoading, isFetching: query.isFetching, isError: query.isError };
};

export default useMortgageRules;
